/**
 * Layout do AmaView (grade CSS):
 * - Desktop: trilho de ícones (64 px) | mapa, e a barra de tempo embaixo. O
 *   painel da categoria (340 px, à esquerda) e o inspetor "Últimos focos" (à
 *   direita, como o cartão do rastreio) FLUTUAM sobre o mapa: abrir ou recolher
 *   não muda o tamanho do mapa nem empurra a imagem (pedido do usuário,
 *   08/10/2026). Nada cobre a imagem além de
 *   chips pequenos (status, ações, zoom, legenda, a assinatura do pyForTraCC —
 *   os créditos dos dados ficam no "Sobre") e o cartão flutuante do rastreio, quando aberto: a vista é presa ao
 *   setor e a exportação recompõe o mapa com legenda e créditos.
 * - Celular (< 768 px): mapa e barra de tempo compacta — ‹ e › grandes nas
 *   pontas, para os dois polegares, e o eixo no meio; o play flutua no meio do
 *   pé do mapa. No topo, a marca (abre o "Sobre") e "Pesquisar" no meio (a
 *   busca por município); na borda esquerda, o trilho de camadas (um toque
 *   liga/desliga; o "⚙" do topo abre a folha inferior com os ajustes); na
 *   coluna da direita, voltar, "⋯", últimos focos e "Próximos dias"; no
 *   pé, o relógio da imagem (abre as opções da animação; embaixo, a idade da
 *   mais recente) e o atalho do período à esquerda, o play no meio e a
 *   previsão das nuvens à direita (29/10/2026: a assinatura do pyForTraCC e a
 *   legenda saíram do mapa no celular — a legenda está no painel da camada; no
 *   "Próximos dias" o topo continua com o chip do relógio da previsão).
 *   Medidas em ui/layoutCelular.ts.
 */
import { useEffect, useLayoutEffect } from 'react';
import { MapLegend } from './legend/MapLegend';
import { useCoreEngine } from './loop/useCoreEngine';
import { useLoopShortcuts } from './loop/useLoopShortcuts';
import { MapView } from './map/MapView';
import { PopupHost } from './map/PopupHost';
import { AbaDaPrevisao, BotaoDaPrevisaoNaColuna, CartaoDaPrevisao, FolhaDaPrevisao } from './previsao/EntradaDaPrevisao';
import { LegendaFlutuante } from './previsao/LegendaFlutuante';
import { iniciarPainelLateral, PainelLateralDaPrevisao } from './previsao/PainelLateralDaPrevisao';
import { SeloPrevisao } from './previsao/SeloPrevisao';
import { AbaDoModelo, BotaoDoModeloNaColuna } from './previsao/TrocaDeModelo';
import { DialogoDoAcesso } from './previsao/AcessoPesquisa';
import { usePrevisaoStore } from './previsao/store';
import { RAIL_WIDTH } from './map/viewControl';
import { useUiStore } from './state/ui';
import { AbaDoRastreio, ForecastChip, PyFortraccMark, TrackingFloat } from './tracking/TrackingFloat';
import { AboutDialog } from './ui/AboutDialog';
import { AvisoSemSatelite } from './ui/AvisoSemSatelite';
import { useFireErrorNotice } from './ui/fireErrorNotice';
import { Inspector, InspectorTab } from './ui/Inspector';
import { LoadingOverlay } from './ui/LoadingOverlay';
import { BrandChip, MapActions, StatusChip } from './ui/MapActions';
import { MapControls } from './ui/MapControls';
import { MobileSheet } from './ui/MobileSheet';
import { ExportDialogSlot, useModuleLayers } from './ui/modules';
import { Rail } from './ui/Rail';
import { RecentFires } from './ui/RecentFires';
import { Sidebar } from './ui/Sidebar';
import { TimeBar } from './ui/TimeBar';
import { Toasts } from './ui/Toasts';
import { TrilhoDeCamadas } from './ui/TrilhoDeCamadas';
import { UpdateNotice } from './ui/UpdateNotice';
import { RelogioDoPe } from './ui/RelogioDoPe';
import { PeDaPrevisao } from './previsao/BarraDaPrevisao';
import { useIsMobile } from './ui/useMediaQuery';

/**
 * Desktop: as abas verticais da borda direita ("Próximos dias", rastreio,
 * últimos focos) numa pilha só, logo abaixo da coluna do zoom (no meio da
 * altura pareciam soltas — pedido do usuário, 18/10/2026) e acima do leitor de
 * coordenadas, 8 px entre elas; em mapa baixo elas encolhem (até 112 px) em vez
 * de se sobrepor. No modo da previsão, só a dela. A dela vem primeiro (pedido
 * do usuário, 21/10/2026): no modo as outras saem, e ela fica no mesmo lugar —
 * o clique que entra e o que volta ao observado são no mesmo ponto.
 */
function AbasDaDireita(props: { modo: boolean; inspectorOpen: boolean }) {
  return (
    <div className="pointer-events-none absolute right-0 z-20 flex flex-col items-end justify-start gap-2 *:pointer-events-auto" style={{ top: 132, bottom: 72 }}>
      <AbaDaPrevisao />
      {/* O modelo do tempo (IFS, AIFS, GFS, ICON), logo abaixo da aba — só no modo. */}
      {props.modo && <AbaDoModelo />}
      {!props.modo && <AbaDoRastreio />}
      {!props.modo && !props.inspectorOpen && <InspectorTab />}
    </div>
  );
}

function ExportHost() {
  const open = useUiStore((s) => s.exportOpen);
  const set = useUiStore((s) => s.set);
  // No "Próximos dias" o mesmo diálogo exporta os passos da previsão (src/previsao/exportar.ts).
  return <ExportDialogSlot open={open} onClose={() => set({ exportOpen: false })} />;
}

export default function App() {
  const mobile = useIsMobile();
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const inspectorOpen = useUiStore((s) => s.inspectorOpen);
  const showSidebar = !mobile && sidebarOpen;
  const showInspector = !mobile && inspectorOpen;
  // "Próximos dias": a legenda é a da previsão, e o rastreio (oculto no mapa) sai de cena.
  const modoPrevisao = usePrevisaoStore((s) => s.modo);
  // O painel lateral da previsão (desktop) ocupa o lugar do das observadas: um por vez.
  // Antes da pintura (useLayoutEffect) e com a guarda no render: os dois nunca aparecem juntos.
  const lateralPrevisao = usePrevisaoStore((s) => s.modo && s.lateral) && !mobile && !sidebarOpen;
  useLayoutEffect(() => {
    if (sidebarOpen) usePrevisaoStore.getState().setLateral(false);
  }, [sidebarOpen]);
  // Ao sair da previsão, o painel das observadas que o dela recolheu volta.
  useEffect(() => iniciarPainelLateral(), []);
  useCoreEngine();
  useLoopShortcuts();
  useModuleLayers();
  // Avisos de falha dos focos (antes montados pela barra superior).
  useFireErrorNotice();

  // Colunas: trilho | mapa. O painel e o inspetor flutuam dentro do <main>, sobre o mapa.
  const columns = mobile ? 'minmax(0, 1fr)' : `${RAIL_WIDTH}px minmax(0, 1fr)`;
  const mapColumn = mobile ? 1 : 2;

  return (
    <div className="grid h-full w-full overflow-hidden bg-ink-950" style={{ gridTemplateRows: 'minmax(0, 1fr) auto', gridTemplateColumns: columns }}>
      {!mobile && <Rail />}
      <main className="relative min-h-0 min-w-0 overflow-hidden" style={{ gridColumn: mapColumn, gridRow: 1 }}>
        <MapView />
        {/* Irmão do mapa: as fichas (PopupHost) já desviam do que está sobre ele. */}
        {showSidebar && <Sidebar />}
        {lateralPrevisao && <PainelLateralDaPrevisao />}
        <PopupHost />
        <LoadingOverlay />
        {!modoPrevisao && <AvisoSemSatelite mobile={mobile} />}
        {mobile ? <BrandChip mobile /> : !sidebarOpen && !lateralPrevisao && <StatusChip />}
        {mobile && <TrilhoDeCamadas />}
        <MapActions mobile={mobile} />
        <MapControls mobile={mobile} />
        {modoPrevisao ? <LegendaFlutuante mobile={mobile} /> : <MapLegend mobile={mobile} />}
        {/* Os "Últimos focos" são observação: saem no "Próximos dias", com os focos. */}
        {!modoPrevisao && (mobile ? <RecentFires /> : showInspector && <Inspector />)}
        {!modoPrevisao && <TrackingFloat mobile={mobile} />}
        {!mobile && <AbasDaDireita modo={modoPrevisao} inspectorOpen={showInspector} />}
        {mobile ? <BotaoDaPrevisaoNaColuna /> : <CartaoDaPrevisao />}
        {mobile && <BotaoDoModeloNaColuna />}
        {mobile && <FolhaDaPrevisao />}
        {!modoPrevisao && <ForecastChip mobile={mobile} />}
        <SeloPrevisao mobile={mobile} />
        {/* No celular a assinatura do pyForTraCC saiu (ocupava o pé; o crédito fica no "Sobre"), e o relógio da imagem ficou no lugar dela. */}
        {!modoPrevisao && !mobile && <PyFortraccMark mobile={mobile} />}
        {mobile && !modoPrevisao && <RelogioDoPe />}
        {mobile && modoPrevisao && <PeDaPrevisao />}
      </main>
      <div style={{ gridColumn: mobile ? 1 : 2, gridRow: 2 }} className="min-w-0">
        <TimeBar mobile={mobile} />
      </div>
      {mobile && <MobileSheet />}
      <Toasts mobile={mobile} />
      <AboutDialog />
      <DialogoDoAcesso />
      <ExportHost />
      <UpdateNotice />
    </div>
  );
}
